/**
 * Diff-based undo/redo history using lightweight patches.
 * Supports replace, insert, remove, and move operations on state paths.
 */

import { getByPath, setByPath } from './state.js';
import { isEditableIdRepairAbsentPatch } from '../shared/collab-protocol.js';

export class History {
  constructor(maxEntries = 100) {
    this.entries = [];
    this.position = -1;
    this.maxEntries = maxEntries;
  }

  get canUndo() {
    return this.position >= 0;
  }

  get canRedo() {
    return this.position < this.entries.length - 1;
  }

  push(forward, reverse, label, options = {}) {
    const entry = { forward, reverse, label, at: Date.now() };
    if (options.identityRepairContext)
      entry.identityRepairContext = structuredClone(options.identityRepairContext);
    if (options.preserveFuture === true) {
      const insertAt = Math.min(this.position + 1, this.entries.length);
      this.entries.splice(insertAt, 0, entry);
      this.position = insertAt;
    } else {
      this.entries.length = this.position + 1;
      this.entries.push(entry);
      this.position++;
    }
    if (this.entries.length > this.maxEntries) {
      this.entries.shift();
      this.position = Math.max(-1, this.position - 1);
    }
  }

  undo(state) {
    if (!this.canUndo) return false;
    const entry = this.entries[this.position];
    applyPatches(state, entry.reverse);
    this.position--;
    return true;
  }

  redo(state) {
    if (!this.canRedo) return false;
    this.position++;
    const entry = this.entries[this.position];
    applyPatches(state, entry.forward);
    return true;
  }

  clear() {
    this.entries = [];
    this.position = -1;
  }
}

export function applyPatches(state, patches) {
  for (const patch of patches) {
    switch (patch.op) {
      case 'replace':
        if (Object.prototype.hasOwnProperty.call(patch, 'absent')) {
          if (!isEditableIdRepairAbsentPatch(patch))
            throw new Error('Invalid absent identity companion');
          const parts = patch.path.split('.');
          const key = parts.pop();
          const parent = getByPath(state, parts.join('.'));
          if (!parent || typeof parent !== 'object')
            throw new Error('Missing identity companion parent');
          delete parent[key];
        } else {
          setByPath(state, patch.path, patch.value);
        }
        break;
      case 'insert': {
        const arr = getByPath(state, patch.path);
        arr.splice(patch.index, 0, structuredClone(patch.item));
        break;
      }
      case 'remove': {
        const arr = getByPath(state, patch.path);
        arr.splice(patch.index, 1);
        break;
      }
      case 'move': {
        const arr = getByPath(state, patch.path);
        const [item] = arr.splice(patch.from, 1);
        arr.splice(patch.to, 0, item);
        break;
      }
      default:
        console.warn(`[History] Unknown patch op: ${patch.op}`);
    }
  }
}
